import React, { useState, useEffect, useMemo } from 'react';
import {
  Input,
  Button,
  Link as FluentLink,
  Tooltip,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import {
  Search24Regular,
  PersonAdd24Regular,
  Dismiss24Regular,
  Person20Filled,
  Building20Filled,
} from '@fluentui/react-icons';
import CustomerHoverCard from './CustomerHoverCard';
import { useDebug } from '../contexts/DebugContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { apiFetch } from '../lib/apiFetch';

type CustomerKind = 'individual' | 'contact' | 'company';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  municipality?: string;
  zipCode?: string;
  country?: string;
  customerKind?: CustomerKind;
  linkedCompanyId?: string | null;
  // Both ids surfaced for debug-mode display. `id` above is whichever the
  // active data source treats as canonical (Booqable id by default; TWM uuid
  // when sourcing from Supabase).
  twmId?: string | null;
  booqableId?: string | null;
  // ISO timestamp of the customer's most recent order. Drives the empty-state
  // "recently used" sort in the dropdown.
  latestOrderAt?: string | null;
}

const deriveCustomerKind = (
  legalType: string | undefined | null,
  companyId: string | undefined | null,
): CustomerKind => {
  if (companyId) return 'contact';
  if (legalType === 'commercial' || legalType === 'Commercial' || legalType === 'Company') return 'company';
  return 'individual';
};

interface CustomerSearchInputProps {
  selectedCustomer: Customer | null;
  onCustomerSelect: (customer: Customer) => void;
  onCustomerClear: () => void;
  onAddCustomer?: () => void;
  lazyLoad?: boolean; // If true, only fetch customers when user interacts with search
  size?: 'small' | 'medium' | 'large';
}

const useStyles = makeStyles({
  searchInput: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  iconButton: {
    minWidth: '40px',
  },
  customerSearchWrapper: {
    position: 'relative',
    flex: 1,
    // Let the field shrink inside its flex row instead of forcing the row
    // wider than the parent — without this the large Input's intrinsic
    // min-width pushes past the section edge on narrow phones.
    minWidth: 0,
  },
  customerDropdown: {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    marginTop: '4px',
    maxHeight: '300px',
    overflowY: 'auto',
    zIndex: 1000,
  },
  customerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  customerKindIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  customerName: {
    fontSize: '14px',
    fontWeight: '600',
  },
  matchedViaContact: {
    fontSize: '13px',
    fontWeight: '400',
    color: tokens.colorNeutralForeground3,
    marginLeft: '6px',
  },
  customerDetails: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  dropdownFooter: {
    ...shorthands.padding('8px', '16px'),
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    position: 'sticky',
    bottom: 0,
  },
  selectedCustomerCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    minHeight: '64px',
    width: '100%',
    boxSizing: 'border-box',
  },
  customerInfoWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    // Allow the wrapper to shrink below its content so the name/email can
    // truncate instead of pushing the clear button off the card.
    minWidth: 0,
  },
  selectedCustomerInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  // Name + email share one baseline-aligned row. The name keeps its weight and
  // truncates last; the email shrinks first and falls away on a narrow card.
  selectedCustomerNameRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    minWidth: 0,
  },
  selectedCustomerName: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    maxWidth: '100%',
  },
  selectedCustomerEmail: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  selectedCustomerDebugIds: {
    fontSize: '13px',
    fontFamily: 'monospace',
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginTop: '4px',
  },
  clearCustomerButton: {
    minWidth: 'auto',
    width: '32px',
    height: '32px',
    ...shorthands.padding('0'),
    flexShrink: 0,
  },
  // Mobile bottom-sheet picker. Floating dropdowns get cramped under the field
  // and the on-screen keyboard covers them; a tall bottom sheet keeps the
  // search pinned at the top with the full list scrolling below.
  bottomSheet: {
    height: '85vh',
  },
  drawerSearch: {
    position: 'sticky',
    top: '0',
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('0', '0', '8px'),
  },
  drawerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '52px',
    ...shorthands.padding('10px', '0'),
    cursor: 'pointer',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    ':last-child': {
      ...shorthands.borderBottom('1px', 'solid', 'transparent'),
    },
  },
  drawerItemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },
  drawerEmpty: {
    ...shorthands.padding('24px', '0'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
  },
  drawerSpinner: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('24px', '0'),
  },
});

export const CustomerSearchInput: React.FC<CustomerSearchInputProps> = ({
  selectedCustomer,
  onCustomerSelect,
  onCustomerClear,
  onAddCustomer,
  lazyLoad = false,
  size = 'medium',
}) => {
  const styles = useStyles();
  const { debugMode } = useDebug();
  const isMobile = useIsMobile();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(!lazyLoad); // Only show loading if not lazy loading
  const [hasFetched, setHasFetched] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerInputRef = React.useRef<HTMLInputElement>(null);
  const customerInfoRef = React.useRef<HTMLDivElement>(null);
  const customerNameRef = React.useRef<HTMLDivElement>(null);
  const [isHoverCardVisible, setIsHoverCardVisible] = React.useState(false);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const showTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Fetch customers from Supabase. Single source of truth — Booqable is
  // mirrored in via /customers/sync (or webhook handlers in prod).
  const fetchCustomers = async () => {
    if (hasFetched) return;

    try {
      setLoading(true);

      const response = await apiFetch(`/customers`);
      if (!response.ok) throw new Error(`Failed to fetch customers: ${response.statusText}`);
      const data = await response.json();
      const mappedCustomers: Customer[] = (data.data || []).map((c: any) => {
        const linkedCompanyId = c.company_id || null;
        return {
          id: c.id,
          name: c.name || 'N/A',
          email: c.email || '',
          phone: c.phone || '',
          address: c.address?.address1 || '',
          city: c.address?.city || '',
          municipality: c.address?.region || '',
          zipCode: c.address?.zipcode || '',
          country: c.address?.country || '',
          customerKind: deriveCustomerKind(c.legal_type, linkedCompanyId),
          linkedCompanyId,
          twmId: c.id,
          booqableId: c.booqable_id || null,
          latestOrderAt: c.latest_order_at || null,
        };
      });

      setCustomers(mappedCustomers);
      setHasFetched(true);
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers on mount if not lazy loading
  useEffect(() => {
    if (!lazyLoad) {
      fetchCustomers();
    }
  }, [lazyLoad]);

  // Orders link to companies/individuals — never contacts. Contacts are only
  // surfaced indirectly: matching one hoists its parent company into results.
  const selectableCustomers = useMemo(
    () => customers.filter(c => c.customerKind !== 'contact'),
    [customers],
  );

  const filteredCustomers = useMemo<Array<Customer & { matchedViaContact?: string }>>(() => {
    // Once a customer is picked the dropdown is replaced by the selected-customer
    // card, so skip the derivation entirely. Selecting sets customerSearch to the
    // full name, which would otherwise run the full filter + contact-hoist — the
    // source of the select lag.
    if (selectedCustomer) return [];

    if (!customerSearch && showCustomerDropdown) {
      // Empty-search suggestion = 5 most recently used customers (ordered by
      // latest_order_at desc). Customers with no orders rank last; among them
      // we fall back to the underlying list order so the suggestions are
      // stable rather than random.
      return [...selectableCustomers]
        .sort((a, b) => {
          const ta = a.latestOrderAt ? new Date(a.latestOrderAt).getTime() : 0;
          const tb = b.latestOrderAt ? new Date(b.latestOrderAt).getTime() : 0;
          return tb - ta;
        })
        .slice(0, 5);
    }

    if (!customerSearch) return [];

    const searchLower = customerSearch.toLowerCase();
    const direct = selectableCustomers.filter(c =>
      c.name.toLowerCase().includes(searchLower) ||
      c.email.toLowerCase().includes(searchLower)
    );

    const seenIds = new Set(direct.map(c => c.id));
    // Index every customer by id once so contact-hoisting is O(n), not O(n²).
    const byId = new Map(customers.map(c => [c.id, c]));
    const hoisted: Array<Customer & { matchedViaContact?: string }> = [];
    for (const c of customers) {
      if (c.customerKind !== 'contact') continue;
      if (!c.linkedCompanyId) continue;
      if (seenIds.has(c.linkedCompanyId)) continue;
      const matches =
        c.name.toLowerCase().includes(searchLower) ||
        c.email.toLowerCase().includes(searchLower);
      if (!matches) continue;
      const company = byId.get(c.linkedCompanyId);
      if (!company) continue;
      seenIds.add(company.id);
      hoisted.push({ ...company, matchedViaContact: c.name });
    }
    return [...direct, ...hoisted];
  }, [customers, selectableCustomers, customerSearch, showCustomerDropdown, selectedCustomer]);

  const handleCustomerSelect = (customer: Customer) => {
    onCustomerSelect(customer);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const handleCustomerSearchChange = (value: string) => {
    setCustomerSearch(value);
    setShowCustomerDropdown(value.length > 0);
    if (value === '') {
      onCustomerClear();
    }
  };

  const handleClearCustomer = () => {
    onCustomerClear();
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  // Mobile bottom-sheet picker controls. showCustomerDropdown is kept true
  // while the sheet is open so filteredCustomers surfaces the recents list on
  // an empty search (same derivation the desktop dropdown uses).
  const openCustomerDrawer = () => {
    if (lazyLoad && !hasFetched && !loading) fetchCustomers();
    setShowCustomerDropdown(true);
    setDrawerOpen(true);
  };

  const closeCustomerDrawer = () => {
    setDrawerOpen(false);
    setShowCustomerDropdown(false);
    setCustomerSearch('');
  };

  // Fluent's Drawer traps focus to the dismiss button on open, which beats the
  // input's `autoFocus`. Claim focus back once the open animation/trap settles
  // so the user can start typing (and Enter-select) immediately.
  React.useEffect(() => {
    if (!drawerOpen) return;
    const t = setTimeout(() => drawerInputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [drawerOpen]);

  const selectCustomerFromDrawer = (customer: Customer) => {
    onCustomerSelect(customer);
    setDrawerOpen(false);
    setShowCustomerDropdown(false);
    setCustomerSearch('');
  };

  const handleCustomerInfoMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    showTimeoutRef.current = setTimeout(() => {
      setIsHoverCardVisible(true);
    }, 150);
  };

  const handleCustomerInfoMouseLeave = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsHoverCardVisible(false);
    }, 150);
  };

  const handleCardClose = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    setIsHoverCardVisible(false);
  };

  const handleCardMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
  };

  if (selectedCustomer) {
    return (
      <>
        <div className={styles.selectedCustomerCard}>
          <div
            ref={customerInfoRef}
            className={styles.customerInfoWrapper}
            onMouseEnter={handleCustomerInfoMouseEnter}
            onMouseLeave={handleCustomerInfoMouseLeave}
          >
            <span
              className={styles.customerKindIcon}
              aria-label={selectedCustomer.customerKind === 'company' ? 'Company' : 'Individual'}
            >
              {selectedCustomer.customerKind === 'company' ? <Building20Filled /> : <Person20Filled />}
            </span>
            <div className={styles.selectedCustomerInfo}>
              <div className={styles.selectedCustomerNameRow}>
                <span ref={customerNameRef} className={styles.selectedCustomerName}>{selectedCustomer.name}</span>
                {selectedCustomer.email && (
                  <span className={styles.selectedCustomerEmail}>{selectedCustomer.email}</span>
                )}
              </div>
              {debugMode && (
                <div className={styles.selectedCustomerDebugIds}>
                  <span>TWM: {selectedCustomer.twmId || '—'}</span>
                  <span>Booqable: {selectedCustomer.booqableId || '—'}</span>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <Tooltip content="Remove customer" relationship="label">
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                className={styles.clearCustomerButton}
                onClick={handleClearCustomer}
                aria-label="Remove customer"
              />
            </Tooltip>
          </div>
        </div>
        {/* Temporarily disabled - positioning issue
        <CustomerHoverCard
          customerId={selectedCustomer.id}
          customerName={selectedCustomer.name}
          customerEmail={selectedCustomer.email}
          anchorRef={customerNameRef}
          isVisible={isHoverCardVisible}
          onClose={handleCardClose}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCustomerInfoMouseLeave}
        />
        */}
      </>
    );
  }

  const handleSearchFocus = () => {
    setShowCustomerDropdown(true);
  };

  const handleSearchBlur = () => {
    // Delay hiding dropdown to allow click events to fire
    setTimeout(() => {
      setShowCustomerDropdown(false);
    }, 200);
  };

  const handleSearchHover = () => {
    // Prefetch customers on hover if lazy loading and haven't fetched yet
    if (lazyLoad && !hasFetched && !loading) {
      fetchCustomers();
    }
  };

  // Mobile: the inline dropdown is cramped and the keyboard covers it, so the
  // field is a tap target that opens a bottom-sheet with the search pinned at
  // top and the full results list scrolling below.
  if (isMobile) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '64px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          className={styles.customerSearchWrapper}
          style={{ cursor: 'pointer' }}
          onClick={openCustomerDrawer}
        >
          <Input
            className={styles.searchInput}
            size={size}
            contentBefore={<Search24Regular />}
            placeholder="Search customer"
            value=""
            readOnly
            aria-label="Search customer"
            // Pointer events flow to the wrapper's onClick so the whole field
            // opens the sheet; the input itself never takes focus/keyboard.
            style={{ pointerEvents: 'none' }}
          />
        </div>
        {onAddCustomer && (
          <Button
            appearance="subtle"
            icon={<PersonAdd24Regular />}
            className={styles.iconButton}
            onClick={onAddCustomer}
            aria-label="Add customer"
          />
        )}
        <Drawer
          type="overlay"
          position="bottom"
          open={drawerOpen}
          onOpenChange={(_, data) => { if (!data.open) closeCustomerDrawer(); }}
          className={styles.bottomSheet}
        >
          <DrawerHeader>
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={closeCustomerDrawer}
                />
              }
            >
              Select customer
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody>
            <div className={styles.drawerSearch}>
              <Input
                input={{ ref: drawerInputRef }}
                className={styles.searchInput}
                contentBefore={<Search24Regular />}
                placeholder="Search by name or email"
                value={customerSearch}
                onChange={(_e, data) => setCustomerSearch(data.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && filteredCustomers.length > 0) {
                    e.preventDefault();
                    selectCustomerFromDrawer(filteredCustomers[0]);
                  }
                }}
              />
            </div>
            {loading ? (
              <div className={styles.drawerSpinner}>
                <Spinner size="small" label="Loading customers…" />
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className={styles.drawerEmpty}>
                {customerSearch ? 'No customers found' : 'No customers'}
              </div>
            ) : (
              filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className={styles.drawerItem}
                  onClick={() => selectCustomerFromDrawer(customer)}
                >
                  <span
                    className={styles.customerKindIcon}
                    aria-label={customer.customerKind === 'company' ? 'Company' : 'Individual'}
                  >
                    {customer.customerKind === 'company' ? <Building20Filled /> : <Person20Filled />}
                  </span>
                  <div className={styles.drawerItemInfo}>
                    <div className={styles.customerName}>
                      {customer.name}
                      {(customer as any).matchedViaContact && (
                        <span className={styles.matchedViaContact}>
                          via {(customer as any).matchedViaContact}
                        </span>
                      )}
                    </div>
                    {customer.email && (
                      <div className={styles.customerDetails}>{customer.email}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </DrawerBody>
        </Drawer>
      </div>
    );
  }

  return (
    // Pin the empty state to the same fixed height as the selected-customer
    // card (64px) so the layout doesn't shift by a couple pixels when a
    // customer is picked or cleared.
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '64px',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div className={styles.customerSearchWrapper}>
        <Input
          className={styles.searchInput}
          size={size}
          contentBefore={<Search24Regular />}
          placeholder="Search customer"
          value={customerSearch}
          onChange={(e, data) => handleCustomerSearchChange(data.value)}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          onMouseEnter={handleSearchHover}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && showCustomerDropdown && filteredCustomers.length > 0) {
              e.preventDefault();
              handleCustomerSelect(filteredCustomers[0]);
            } else if (e.key === 'Escape') {
              setShowCustomerDropdown(false);
            }
          }}
        />
        {showCustomerDropdown && filteredCustomers.length > 0 && (
          // Suppress the default mousedown focus shift: without this, pressing a
          // row blurs the Input first (firing handleSearchBlur, which hides the
          // dropdown), so the row's onClick is eaten and selection takes two
          // clicks. preventDefault keeps focus on the Input so the first click
          // selects cleanly.
          <div
            className={styles.customerDropdown}
            onMouseDown={(e) => e.preventDefault()}
          >
            {filteredCustomers.map((customer) => (
              <div
                key={customer.id}
                className={styles.customerItem}
                onClick={() => handleCustomerSelect(customer)}
              >
                <span
                  className={styles.customerKindIcon}
                  aria-label={customer.customerKind === 'company' ? 'Company' : 'Individual'}
                >
                  {customer.customerKind === 'company' ? <Building20Filled /> : <Person20Filled />}
                </span>
                <div className={styles.customerName}>
                  {customer.name}
                  {(customer as any).matchedViaContact && (
                    <span className={styles.matchedViaContact}>
                      via {(customer as any).matchedViaContact}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {!customerSearch && selectableCustomers.length > filteredCustomers.length && (
              <div className={styles.dropdownFooter}>
                Showing {filteredCustomers.length} of {selectableCustomers.length} — type to search
              </div>
            )}
          </div>
        )}
      </div>
      {onAddCustomer && (
        <Button
          appearance="subtle"
          icon={<PersonAdd24Regular />}
          className={styles.iconButton}
          onClick={onAddCustomer}
          aria-label="Add customer"
        />
      )}
    </div>
  );
};

export default CustomerSearchInput;
